import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Project, User } from '../../store/types'
import { t } from 'i18next'
import { useGetLightLeadersQuery, useGetLightProjectLeadersQuery } from '../../store/redux/reducers/usersSlice'
import ConfirmButton from '../Button/ConfirmButton'
import { useDebouncedScroll } from '../../store/utils/useDebouncedScroll'

export type ChangeLeaderDialogVariant = 'roleRemoved' | 'statusBlocked' | 'statusDeleted'

interface ChangeLeaderDialogProps {
  open: boolean
  onClose: () => void
  handleSubmitEditWithLeaderChange: (substituteLeaderId: string, substituteProjectLeaderId: string) => void
  employeesOfLeader: User[]
  projectsOfLeader: Project[]
  isLoading?: boolean
  editedUser: User | null
  variant?: ChangeLeaderDialogVariant
}

const ChangeLeaderDialog = ({
  open,
  onClose,
  handleSubmitEditWithLeaderChange,
  employeesOfLeader,
  projectsOfLeader,
  isLoading,
  editedUser,
  variant = 'roleRemoved',
}: ChangeLeaderDialogProps) => {
  const theme = useTheme()

  const [substituteLeaderId, setSubstituteLeaderId] = useState<string>('')
  const [substituteProjectLeaderId, setSubstituteProjectLeaderId] = useState<string>('')

  const [leaderPage, setLeaderPage] = useState(0)
  const [projectLeaderPage, setProjectLeaderPage] = useState(0)
  const [displayedLeaders, setDisplayedLeaders] = useState<User[]>([])
  const [displayedProjectLeaders, setDisplayedProjectLeaders] = useState<User[]>([])

  const { data: employeeLeadersData } = useGetLightLeadersQuery(
    { page: leaderPage, size: 20, employeeStatus: 'ACTIVE' },
    { skip: employeesOfLeader.length === 0 }
  )

  const { data: leadersData } = useGetLightProjectLeadersQuery(
    { page: projectLeaderPage, size: 20, employeeStatus: 'ACTIVE' },
    { skip: projectsOfLeader.length === 0 }
  )

  useEffect(() => {
    if (employeeLeadersData) {
      const newLeaders = employeeLeadersData.content.filter(leader => leader.id !== editedUser?.id)
      setDisplayedLeaders(prev => {
        if (leaderPage === 0) return newLeaders
        const existingIds = new Set(prev.map(l => l.id))
        return [...prev, ...newLeaders.filter(l => !existingIds.has(l.id))]
      })
    }
  }, [employeeLeadersData, leaderPage, editedUser?.id])

  useEffect(() => {
    if (leadersData) {
      const newLeaders = leadersData.content.filter(leader => leader.id !== editedUser?.id)
      setDisplayedProjectLeaders(prev => {
        if (projectLeaderPage === 0) return newLeaders
        const existingIds = new Set(prev.map(l => l.id))
        return [...prev, ...newLeaders.filter(l => !existingIds.has(l.id))]
      })
    }
  }, [leadersData, projectLeaderPage, editedUser?.id])

  const handleClose = () => {
    setLeaderPage(0)
    setProjectLeaderPage(0)
    setSubstituteLeaderId('')
    setSubstituteProjectLeaderId('')
    onClose()
  }

  const handleFetchMoreLeaders = useCallback(() => {
    if (!employeeLeadersData?.last) {
      setLeaderPage(prev => prev + 1)
    }
  }, [employeeLeadersData?.last])

  const handleFetchMoreProjectLeaders = useCallback(() => {
    if (!leadersData?.last) {
      setProjectLeaderPage(prev => prev + 1)
    }
  }, [leadersData?.last])

  const handleLeaderMenuScroll = useDebouncedScroll(handleFetchMoreLeaders)
  const handleProjectLeaderMenuScroll = useDebouncedScroll(handleFetchMoreProjectLeaders)

  const handleSubLeaderChange = (event: SelectChangeEvent) => {
    setSubstituteLeaderId(event.target.value)
  }

  const handleSubProjectLeaderChange = (event: SelectChangeEvent) => {
    setSubstituteProjectLeaderId(event.target.value)
  }

  const getLeaderTitle = () => {
    switch (variant) {
      case 'statusBlocked':
        return t('page.usersProfile.dialog.leaderChange.blockedTitle')
      case 'statusDeleted':
        return t('page.usersProfile.dialog.leaderChange.deletedTitle')
      default:
        return t('page.usersProfile.dialog.leaderChange.title')
    }
  }

  const getProjectLeaderTitle = () => {
    switch (variant) {
      case 'statusBlocked':
        return t('page.usersProfile.dialog.projectLeaderChange.blockedTitle')
      case 'statusDeleted':
        return t('page.usersProfile.dialog.projectLeaderChange.deletedTitle')
      default:
        return t('page.usersProfile.dialog.projectLeaderChange.title')
    }
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        elevation: 0,
        sx: {
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(2),
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(2),
          maxWidth: 500,
          bgcolor: 'background.elevation1',
        },
      }}
    >
      {employeesOfLeader.length > 0 && (
        <>
          <DialogTitle color="text.highEmphasis">{getLeaderTitle()}</DialogTitle>
          <DialogContent color="text.base">
            <DialogContentText sx={{ pb: 1 }}>
              <Stack gap={2}>
                <Typography component={'span'} sx={{ color: theme.palette.warning.main }}>
                  {t('page.usersProfile.dialog.leaderChange.numberOfEmployees', { count: employeesOfLeader.length })}
                </Typography>
                <Typography component={'span'} color="text.base">
                  {t('page.usersProfile.dialog.leaderChange.content')}
                </Typography>
              </Stack>
            </DialogContentText>
            <FormControl size="small" sx={{ width: '100%', my: 1 }}>
              <InputLabel id="leader-simple-select-label">{t('page.usersProfile.form.headers.leader')}</InputLabel>
              <Select
                label={t('page.usersProfile.form.headers.leader')}
                onChange={handleSubLeaderChange}
                value={substituteLeaderId}
                MenuProps={{ PaperProps: { onScroll: handleLeaderMenuScroll } }}
              >
                <MenuItem key={0} value={''}>{`-`}</MenuItem>
                {displayedLeaders.map(leader => (
                  <MenuItem key={leader.id} value={leader.id}>{`${leader.firstName} ${leader.lastName}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
        </>
      )}
      {projectsOfLeader.length > 0 && (
        <>
          <DialogTitle color="text.highEmphasis">{getProjectLeaderTitle()}</DialogTitle>
          <DialogContent color="text.base">
            <DialogContentText sx={{ pb: 1 }}>
              <Stack gap={2}>
                <Typography component={'span'} sx={{ color: theme.palette.warning.main }}>
                  {t('page.usersProfile.dialog.projectLeaderChange.numberOfProjects', {
                    count: projectsOfLeader.length,
                  })}
                </Typography>
                <Typography component={'span'} color="text.base">
                  {t('page.usersProfile.dialog.projectLeaderChange.content')}
                </Typography>
              </Stack>
            </DialogContentText>
            <FormControl size="small" sx={{ width: '100%', my: 1 }}>
              <InputLabel id="leader-simple-select-label">{t('page.usersProfile.form.headers.leader')}</InputLabel>
              <Select
                label={t('page.usersProfile.form.headers.leader')}
                onChange={handleSubProjectLeaderChange}
                value={substituteProjectLeaderId}
                MenuProps={{ PaperProps: { onScroll: handleProjectLeaderMenuScroll } }}
              >
                <MenuItem key={0} value={''}>{`-`}</MenuItem>
                {displayedProjectLeaders.map(leader => (
                  <MenuItem key={leader.id} value={leader.id}>{`${leader.firstName} ${leader.lastName}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
        </>
      )}
      <DialogActions sx={{ gap: 2 }}>
        <Button onClick={handleClose} size="small">
          {t('page.adminsPage.dialog.return')}
        </Button>
        <ConfirmButton
          text={t('page.usersProfile.header.save')}
          onClick={() => handleSubmitEditWithLeaderChange(substituteLeaderId, substituteProjectLeaderId)}
          isLoadingButton={true}
          isLoading={isLoading ?? false}
          isDisabled={isLoading ?? false}
        />
      </DialogActions>
    </Dialog>
  )
}

export default ChangeLeaderDialog
